import React from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from '@components/moleculs/autocomplete';
import type { AutocompleteProps } from '@components/moleculs/autocomplete/types';
import { CountryFlagAdornment } from '@components/moleculs/country-flag-adornment';
import { CountryOption } from '@components/moleculs/country-option';
import { TextField } from '@components/moleculs/text-field';
import type { CountryAutocompleteProps } from '@components/organisms/country-autocomplete/types';
import { useCountryOptions } from '@hooks/options/use-country-options';
import type { AutocompleteOption } from '@models/types';

// The molecule exports `Autocomplete` as a bare generic function, which TS can't reconcile with a
// spread props object + explicit type args (tags-autocomplete papers over this with
// @ts-expect-error, which we avoid). Monomorphise it to the concrete country shape via an annotated
// assignment (no cast) so the spread type-checks.
const CountryBaseAutocomplete: React.FC<
    AutocompleteProps<AutocompleteOption, false, false, false>
> = Autocomplete;

// Single-select country picker: encapsulates the flag option, the input flag adornment and the
// by-value equality so consumers pass only `value`/`onChange` (options default to the shared list).
// Replaces the copy-pasted `renderOption`/`renderInput` in the global filters and the old
// `useCountryAutocomplete` hook.
export const CountryAutocomplete = ({
    value,
    options,
    placeholder,
    inputSize = 'm',
    classes,
    isOptionEqualToValue,
    disabledSuffix,
    ...rest
}: CountryAutocompleteProps) => {
    const { t } = useTranslation();
    const countryOptions = useCountryOptions();

    // Small sizes get the compact option layout; standard sizes keep MUI's roomy default.
    const isCompact = inputSize === 's' || inputSize === 's/m';

    const autocompleteProps: AutocompleteProps<AutocompleteOption, false, false, false> = {
        ...rest,
        value,
        options: options ?? countryOptions,
        classes: { popper: 'filter-country-autocomplete', ...classes },
        isOptionEqualToValue:
            isOptionEqualToValue ?? ((option, selected) => option?.value === selected?.value),
        renderOption: ({ key, className, ...optionProps }, option) => (
            <CountryOption
                key={key}
                className={className}
                isShowCheckbox={false}
                compact={isCompact}
                // Derive from the same predicate MUI uses to disable the option (never aria-disabled).
                disabled={rest.getOptionDisabled?.(option)}
                disabledSuffix={disabledSuffix}
                option={option}
                {...optionProps}
            />
        ),
        renderInput: (params) => (
            <TextField
                {...params}
                inputSize={inputSize}
                InputProps={{
                    ...params.slotProps.input,
                    startAdornment: <CountryFlagAdornment countryCode={value?.value} />,
                }}
                placeholder={placeholder ?? t('filters.countryPlaceholder')}
            />
        ),
    };

    return <CountryBaseAutocomplete {...autocompleteProps} />;
};
